import SidebarComponent from 'components/layouts/sidebar';
import {useSelector} from 'react-redux';
import {orgSelector, userSelector} from '../redux/selectors';
import {Spinner, Table, Tooltip} from 'flowbite-react';
import {useTranslation} from 'react-i18next';
import Header from 'components/header';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PieChart from 'components/commonComponent/chart/pieChart';
import ReportTable from 'components/commonComponent/chart/reportTable';
import { useQuery } from '@tanstack/react-query';
import { defaultpageCount, permissionKeys } from 'utils/constants';
import { getItemSaleFilter, getItemShrinkageFilter, getLowHighStockFilter } from 'api/dashboardApi';
import { checkOrgAdmin, checkPermission, getUserOrg } from 'utils/utils';
import NoticeBanner from 'components/commonComponent/banner/noticeBanner';
import SpinnerCustom from 'components/spinnerCustom';

const DashboardPage = () => {
    const [t] = useTranslation();
    const {userInfo} = useSelector(userSelector);
    const building = require('../../src/assets/image/svg/building.svg').default;
    const {organizationId} = useSelector(orgSelector);
    const [lowStocks, setLowStocks] = useState([]);
    const [hasMoreLow, setHasMoreLow] = useState(true);
    const [currentPageLowStock, setCurrentPageLowStock] = useState(0);
    const [currentPageHighStock, setCurrentPageHighStock] = useState(0);
    const [highStocks, setHighStocks] = useState([]);
    const [hasMoreHigh, setHasMoreHigh] = useState(true);
    const [locationData, setLocationData] = useState<any>([]);
    const [categoryData, setCategoryData] = useState<any>([]);
    const [reportShrinkageData, setReportShrinkageData] = useState<any>([]);
    const [pageCount, setPageCount] = useState(defaultpageCount);

    const {WRITE_LOCATION, PLATFORM_ADMIN} = permissionKeys;
    const isAdmin = checkPermission(userInfo, "Organization", [PLATFORM_ADMIN], organizationId);
    const isOrgAdmin = checkOrgAdmin(userInfo, organizationId, isAdmin);

    const getRegions = useMemo(() => {
        const userOrg = userInfo?.organizations?.find((o: any) => o.id === organizationId);
        const orgRegions = userOrg?.regions;
        let regionIds = '';
        if (orgRegions) {
            for (let index = 0; index < orgRegions.length; index++) {
                const region = orgRegions[index];
                if (regionIds === '') {
                    regionIds = region.id;
                } else {
                    regionIds = `${regionIds},${region.id}`;
                }
            }
        }
        return regionIds;
    }, [userInfo, organizationId]);

    const {data: lowStockData, isFetching: isFetchingLow, refetch: refetchLow} = useQuery(
        ['getLowStockFilter', organizationId, currentPageLowStock, pageCount],
        () => getLowHighStockFilter({
            page: currentPageLowStock,
            limit: pageCount,
            organizationId: organizationId,
            stockType: 'LowStock',
            userOrgId: isOrgAdmin === true ? null : getUserOrg(userInfo, organizationId)?.userOrganizationId, 
            regionIds: isOrgAdmin === true ? '' : getRegions
        }),
        {
            staleTime: Infinity,
            keepPreviousData: true
        },
    );

    const {data: highStockData, isFetching: isFetchingHigh, refetch: refetchHigh} = useQuery(
        ['getHighStockFilter', organizationId, currentPageHighStock, pageCount],
        () => getLowHighStockFilter({
            page: currentPageHighStock,
            limit: pageCount,
            organizationId: organizationId,
            stockType: 'HighStock',
            userOrgId: isOrgAdmin === true ? null : getUserOrg(userInfo, organizationId)?.userOrganizationId, 
            regionIds: isOrgAdmin === true ? '' : getRegions
        }),
        {
            staleTime: Infinity,
            keepPreviousData: true
        },
    );

    useEffect(() => {
        setCurrentPageHighStock(0);
        setHighStocks([]);
        setHasMoreHigh(true);
        setCurrentPageLowStock(0);
        setLowStocks([]);
        setHasMoreLow(true);
    }, [organizationId]);


    const {data: pieData, isLoading: isLoadingPieData} = useQuery(
        ['getItemSaleFilter', organizationId],
        () => getItemSaleFilter({
            page: 0,
            limit: pageCount,
            organizationId: organizationId,
            userOrgId: isOrgAdmin === true ? null : getUserOrg(userInfo, organizationId)?.userOrganizationId, 
            regionIds: isOrgAdmin === true ? '' : getRegions
        }),
        {
            staleTime: Infinity,
        },
    );

    const {data: shrinkageData, isLoading: isLoadingShrinkage} = useQuery(
        ['getItemShrinkageFilter', organizationId],
        () => getItemShrinkageFilter({
            page: 0,
            limit: pageCount,
            organizationId: organizationId,
            userOrgId: isOrgAdmin === true ? null : getUserOrg(userInfo, organizationId)?.userOrganizationId, 
            regionIds: isOrgAdmin === true ? '' : getRegions
        }),
        {
            staleTime: Infinity,
        },
    );

    useEffect(() => {
        if (pieData) {
            setLocationData(pieData.data.locationData);
            // Normalize categoryData labels: replace null with "None"
            const normalizedCategoryData = {
                ...pieData.data.categoryData,
                labels: pieData.data.categoryData?.labels?.map((label: string | null) => label ?? "None") ?? []
            };
            setCategoryData(normalizedCategoryData);
            setLegend(locationData?.labels);
            setColors(makeColors(locationData?.labels?.length));
        }
        // eslint-disable-next-line
    }, [pieData])

    useEffect(() => {
        if (shrinkageData) {
            setReportShrinkageData(shrinkageData.data);
        }
        // eslint-disable-next-line
    }, [shrinkageData])

    const makeColors = useCallback((n: number) =>
        Array.from({ length: n }, (_, i) => `hsl(${Math.round((360 / n) * i)}, 45%, 55%)`), []);

    const [mode, setMode] = useState<'location' | 'category'>('location');
    const [legend, setLegend] = useState<string[]>(locationData?.labels);
    const [colors, setColors] = useState<string[]>(makeColors(locationData?.labels?.length));
    const [isLegendExpanded, setIsLegendExpanded] = useState(false);
    const [updatePie, setUpdatePie] = useState<
        ((labels: string[], values: number[], colors?: string[]) => void) | null
    >(null);

    useEffect(() => {
        if (!updatePie) return;
        const d = mode === 'location' ? locationData : categoryData;
    
        const cols = makeColors(d?.labels?.length);
        setColors(cols);
        setLegend(d?.labels);
        setIsLegendExpanded(false); // Reset expansion when mode changes
    
        updatePie(d?.labels, d?.values, cols);
        // eslint-disable-next-line
    }, [mode, updatePie, locationData, categoryData]);

    useEffect(() => {
        if (lowStockData) {
            const newItems = lowStockData.data.data ?? lowStockData.data.data ?? [];

            if (newItems.length < defaultpageCount) setHasMoreLow(false);
        
            setLowStocks((prev) =>
            {
                if (currentPageLowStock === 0) {
                    return newItems
                } else {
                    const tempLow = removeDuplicates([...prev, ...newItems]);
                    return tempLow;
                }
            }
            );
        }
        // eslint-disable-next-line
    }, [lowStockData, currentPageLowStock])

    useEffect(() => {
        if (highStockData) {
            const newItems = highStockData.data.data ?? highStockData.data.data ?? [];

            if (newItems.length < defaultpageCount) setHasMoreHigh(false);
        
            setHighStocks((prev) =>
                {
                    if(currentPageHighStock === 0) {
                        return newItems
                    }else {
                        const tempHigh = removeDuplicates([...prev, ...newItems]);
                        return tempHigh;
                    }
                }
            );
        }
        // eslint-disable-next-line
    }, [highStockData, currentPageHighStock])

    /* ------------------ infinite scroll ------------------ */
    const containerHighRef = useRef<HTMLDivElement>(null);
    const sentinelHighRef = useRef<HTMLDivElement>(null);
    const containerLowRef = useRef<HTMLDivElement>(null);
    const sentinelLowRef = useRef<HTMLDivElement>(null);

    const loadMoreHigh = useCallback(() => {

        if (isFetchingHigh || !hasMoreHigh) return;
        setCurrentPageHighStock((p) => p + 1);
    }, [isFetchingHigh, hasMoreHigh]);

    const loadMoreLow = useCallback(() => {

        if (isFetchingLow || !hasMoreLow) return;
        setCurrentPageLowStock((p) => p + 1);
    }, [isFetchingLow, hasMoreLow]);

    useEffect(() => {
        const root = containerHighRef.current;
        const sentinel = sentinelHighRef.current;
        if (!root || !sentinel) return;

        let lastCallTime = 0;
        const observer = new IntersectionObserver(
            (entries) => {
            const e = entries[0];
            const now = Date.now();
            if (e.isIntersecting && now - lastCallTime > 200) {
                lastCallTime = now;
                loadMoreHigh();
            }
            },
            { root, threshold: 0.1, rootMargin: '50px' }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [loadMoreHigh]);

    useEffect(() => {
        const root = containerLowRef.current;
        const sentinel = sentinelLowRef.current;
        if (!root || !sentinel) return;

        let lastCallTime = 0;
        const observer = new IntersectionObserver(
            (entries) => {
            const e = entries[0];
            const now = Date.now();
            if (e.isIntersecting && now - lastCallTime > 200) {
                lastCallTime = now;
                loadMoreLow();
            }
            },
            { root, threshold: 0.1, rootMargin: '50px' }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [loadMoreLow]);

    const removeDuplicates = useCallback((items: any) => {
        const seen = new Set<string>();
        return items.filter((item: any) => {
          const key = `${item.locationId}-${item.itemId}`;
          if (seen.has(key)) {
            return false;
          }
          seen.add(key);
          return true;
        });
    }, [])

    if (!userInfo?.id) {
        return (
            <Spinner/>
        );
    }

    return (
        <>
            <NoticeBanner />
            <Header/>
            <div className="w-full overflow-hidden flex bg-white h-page relative">
                <SidebarComponent isDashboard={true}/>
                <div className="relative w-full flex flex-col main-content" style={{ willChange: 'scroll-position' }}>
                    {userInfo?.organizations.length === 0 
                    ? 
                    (
                        <div className="h-full pt-16">
                            <div className="mb-4 flex justify-center">
                                <img alt="MileMarker logo" src={building}/>
                            </div>
                            <p className="text-2xl font-semibold text-center mb-4">{t('dashboard.notification')}</p>
                            <p className="text-sm font-normal text-center	">{t('dashboard.note')}</p>
                        </div>
                    )
                    :
                    (
                        <>
                            <section className="grid grid-cols-1 lg:grid-cols-[30%_70%] gap-6">
                                {/* Item Sales */}
                                <div className="bg-white rounded-xl shadow-sm p-6">
                                    {/* Title */}
                                    <h3 className="text-xl font-semibold text-slate-800">
                                        {t('dashboard.itemSale')}
                                    </h3>
                                    {isLoadingPieData ? (<SpinnerCustom className='h-[280px]' />) : 
                                    (
                                        <>
                                            {/* Radios */}
                                            <div className="mt-3 flex items-center justify-center gap-6 text-sm">
                                                <label className="inline-flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="mode"
                                                    value="location"
                                                    checked={mode === 'location'}
                                                    onChange={() => setMode('location')}
                                                    className="accent-amber-600"
                                                />
                                                Location
                                                </label>

                                                <label className="inline-flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="mode"
                                                    value="category"
                                                    checked={mode === 'category'}
                                                    onChange={() => setMode('category')}
                                                    className="accent-slate-600"
                                                />
                                                Category
                                                </label>
                                            </div>

                                            {/* Pie chart + legend */}
                                            {locationData && locationData?.values?.length > 0 && (
                                                <div className="mt-4">
                                                <div className="mx-auto relative w-[320px] h-[320px]">
                                                    <PieChart
                                                    labels={locationData.labels}
                                                    values={locationData.values}
                                                    colors={colors}
                                                    className="relative w-[320px] h-[320px] overflow-visible"
                                                    onReady={({ update }) => setUpdatePie(() => update)}
                                                    />
                                                </div>

                                                <div>
                                                    <ul className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-y-2 gap-x-4 text-sm text-slate-700">
                                                        {(isLegendExpanded ? legend : legend?.slice(0, 16))?.map((name, i) => (
                                                            <li key={`${name}-${i}`} className="flex items-center gap-2">
                                                                <span
                                                                className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                                                                style={{ background: colors[i] }}
                                                                />
                                                                <span className="truncate whitespace-nowrap max-w-[220px]">
                                                                    <Tooltip content={name}>{name.includes('-') ? name.split('-')[0] : name}</Tooltip>
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    {legend && legend.length > 16 && (
                                                        <button
                                                            onClick={() => setIsLegendExpanded(!isLegendExpanded)}
                                                            className="mt-3 text-sm text-slate-600 hover:text-slate-800 font-medium underline"
                                                        >
                                                            {isLegendExpanded ? 'See Less' : 'See More'}
                                                        </button>
                                                    )}
                                                </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Shrinkage and Sales */}
                                <div>
                                    <ReportTable data={reportShrinkageData} title={t('dashboard.shrinkageAndSale')} color='#8979FF' linearFrom='rgba(137, 121, 255, 0.3)' linearTo='rgba(137, 121, 255, 0.05)' loading={isLoadingShrinkage}/>
                                </div>
                            </section>

                            <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                                {/* Low Stock */}
                                <div className="bg-white rounded-xl shadow-sm p-6">
                                    <h3 className="text-xl font-semibold text-slate-800 mb-3">{t('dashboard.lowStock')}</h3>
                                    <div
                                            ref={containerLowRef}
                                            className="overflow-y-auto max-h-[360px] rounded-lg text-sm"
                                            style={{ willChange: 'scroll-position', transform: 'translateZ(0)' }}
                                            >
                                            <Table className="border-b">
                                                    <Table.Head className="sticky top-0 bg-stale-50 text-xs text-gray-900 border-b border-gray-200">
                                                        <Table.HeadCell className="text-left p-2">
                                                        {t('dashboard.location')}
                                                        </Table.HeadCell>
                                                        <Table.HeadCell className="text-left p-2">
                                                        {t('dashboard.item')}
                                                        </Table.HeadCell>
                                                        <Table.HeadCell className="px-0 py-0 text-center">
                                                        {t('dashboard.fourWeekAvgSales')}
                                                        </Table.HeadCell>
                                                        <Table.HeadCell className="px-0 py-0 text-center">
                                                        {t('dashboard.inventoryLevel')}
                                                        </Table.HeadCell>
                                                    </Table.Head>
                                                    <Table.Body>
                                                        {lowStocks?.map((r: any, index) => (
                                                            <Table.Row key={`row-${index + 1}`} className="border-b border-gray-200 text-black">
                                                                <Table.Cell className="p-2">{r.locationName}</Table.Cell>
                                                                <Table.Cell className="p-2">{r.itemName}</Table.Cell>
                                                                <Table.Cell className={`p-2 text-center`}>{r.fourWeekAvgSales}</Table.Cell>
                                                                <Table.Cell className={`p-2 text-center`}>{r.quantity}</Table.Cell>
                                                            </Table.Row>
                                                        ))}
                                                    </Table.Body>
                                            </Table>

                                            {/* infinite scroll sentinel */}
                                            <div ref={sentinelLowRef} className="h-6" />
                                            <div className="py-2 text-center text-xs text-slate-400">
                                                {isFetchingLow ? <SpinnerCustom className="h-[180px]"/> : !hasMoreLow ? '— No more data —' : ''}
                                            </div>
                                            </div>
                                </div>

                                {/* High Stock */}
                                <div className="bg-white rounded-xl shadow-sm p-6">
                                    <h3 className="text-xl font-semibold text-slate-800 mb-3">{t('dashboard.highStock')}</h3>
                                    <div
                                            ref={containerHighRef}
                                            className="overflow-y-auto max-h-[360px] rounded-lg text-sm"
                                            style={{ willChange: 'scroll-position', transform: 'translateZ(0)' }}
                                            >
                                            <Table className="w-full">
                                                <Table.Head className="sticky top-0 bg-slate-50 text-xs text-gray-900 border-b border-gray-200 z-10">
                                                <Table.HeadCell className="text-left p-2">
                                                    {t('dashboard.location')}
                                                </Table.HeadCell>
                                                <Table.HeadCell className="text-left p-2">
                                                    {t('dashboard.item')}
                                                </Table.HeadCell>
                                                <Table.HeadCell className="text-center px-0 py-0">
                                                    {t('dashboard.fourWeekAvgSales')}
                                                </Table.HeadCell>
                                                <Table.HeadCell className="text-center px-0 py-0">
                                                    {t('dashboard.inventoryLevel')}
                                                </Table.HeadCell>
                                                </Table.Head>

                                                <Table.Body>
                                                {highStocks?.map((r: any, index) => (
                                                    <Table.Row
                                                    key={`row-high-${index}`}
                                                    className="border-b border-gray-200 text-black"
                                                    >
                                                    <Table.Cell className="p-2">{r.locationName}</Table.Cell>
                                                    <Table.Cell className="p-2">{r.itemName}</Table.Cell>
                                                    <Table.Cell className={`p-2 text-center`}>
                                                        {r.fourWeekAvgSales}
                                                    </Table.Cell>
                                                    <Table.Cell className={`p-2 text-center`}>
                                                        {r.quantity}
                                                    </Table.Cell>
                                                    </Table.Row>
                                                ))}
                                                </Table.Body>
                                            </Table>

                                            <div ref={sentinelHighRef} className="h-6" />
                                            <div className="py-2 text-center text-xs text-slate-400">
                                                {isFetchingHigh ? <SpinnerCustom className="h-[180px]"/> : !hasMoreHigh ? '— No more data —' : ''}
                                            </div>
                                            </div>
                                </div>
                            </section>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};
export default DashboardPage;
